import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Search, Download } from 'lucide-react';
import api from '../api'; // Import the existing api instance

const JuspayLogsView = () => {
  const [logs, setLogs] = useState([]);
  const [partners, setPartners] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // State for search filters
  const [orderId, setOrderId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [fromDate, setFromDate] = useState('2026-03-01');
  const [toDate, setToDate] = useState('2026-03-11');
  const [nameSearchTerm, setNameSearchTerm] = useState('');

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const partnersResponse = await api.get('/partners');
      const partnersData = Array.isArray(partnersResponse.data?.partners) ? partnersResponse.data.partners : [];
      const partnerMap = new Map(partnersData.map(p => [p.id.toString(), { name: p.partner_name, phone: p.partner_number }]));
      setPartners(partnerMap);

      const fromDateISO = fromDate ? new Date(fromDate).toISOString() : '2026-03-01T00:00:00Z';
      const toDateISO = toDate ? new Date(toDate).toISOString().replace('T00:00:00.000Z', 'T23:59:59Z') : '2026-03-11T23:59:59Z';

      const params = new URLSearchParams({
        orderId,
        customerId,
        amount,
        fromDate: fromDateISO,
        toDate: toDateISO,
      });

      const JUSPAY_API_BASE_URL = process.env.NODE_ENV === 'production'
        ? 'https://stag-fleet.snapecab.com'
        : '';

      const logsResponse = await axios.get(
        `${JUSPAY_API_BASE_URL}/wallet/webhook/logs?${params.toString()}`,
        {
          headers: {
            'Client-Id': '1234567890',
            'Api-Key': 'abcdef1234567890',
          },
        }
      );
      
      const responseData = Array.isArray(logsResponse.data) ? logsResponse.data : [];

      const filteredData = responseData.filter(log => {
        const id = parseInt(log.customerId, 10);
        return id >= 0 && id <= 300;
      });
      setLogs(filteredData);

    } catch (err) {
      setError('Failed to fetch data. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [orderId, customerId, amount, fromDate, toDate]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchInitialData();
  };

  const handleDownload = () => {
    if (filteredLogs.length === 0) {
      alert("No data to download.");
      return;
    }

    const headers = ['Order ID', 'Customer ID', 'Driver Name', 'Driver Phone', 'Amount', 'Order Status', 'Created At'];
    const csvRows = [headers.join(',')];

    filteredLogs.forEach(log => {
      const driver = partners.get(log.customerId.toString());
      const row = [
        `"${log.orderId}"`,
        `"${log.customerId}"`,
        `"${driver?.name || 'N/A'}"`,
        `"${driver?.phone || 'N/A'}"`,
        log.amount,
        `"${log.orderStatus}"`,
        `"${new Date(log.createdAt).toLocaleString()}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'juspay_logs.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredLogs = useMemo(() => {
    if (!nameSearchTerm) {
      return logs;
    }
    return logs.filter(log => {
      const driver = partners.get(log.customerId.toString());
      return driver && driver.name.toLowerCase().includes(nameSearchTerm.toLowerCase());
    });
  }, [logs, nameSearchTerm, partners]);

  return (
    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl p-6 sm:p-8">
      <div className="mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 dark:text-white">
          Juspay Webhook Logs
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          View and search for Juspay webhook events.
        </p>
      </div>

      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row flex-wrap items-center justify-start gap-4 mb-6">
        <input
            type="text"
            placeholder="Order ID"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            className="pl-4 pr-4 py-2 border rounded-lg w-full sm:w-auto"
        />
        <input
            type="text"
            placeholder="Customer ID"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            className="pl-4 pr-4 py-2 border rounded-lg w-full sm:w-auto"
        />
        <input
            type="text"
            placeholder="Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="pl-4 pr-4 py-2 border rounded-lg w-full sm:w-auto"
        />
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            min={fromDate}
            className="px-4 py-2 border rounded-lg"
          />
        </div>
        <button
          type="submit"
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg"
          disabled={loading}
        >
          <Search className="w-5 h-5" />
          <span>{loading ? 'Searching...' : 'Search'}</span>
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg"
          disabled={loading || filteredLogs.length === 0}
        >
          <Download className="w-5 h-5" />
          <span>Download CSV</span>
        </button>
      </form>

      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by Driver Name..."
          value={nameSearchTerm}
          onChange={(e) => setNameSearchTerm(e.target.value)}
          className="pl-4 pr-4 py-2 border rounded-lg w-full sm:w-1/3"
        />
      </div>

      {error && <p className="text-red-500 bg-red-100 p-3 rounded-lg mb-4">{error}</p>}

      <div className="overflow-x-auto">
        <table className="min-w-full leading-normal">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Order ID</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Customer ID</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Driver Name</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Driver Phone</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Amount</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Order Status</th>
              <th className="px-6 py-4 border-b-2 text-left text-xs font-semibold uppercase">Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="text-center py-10">Loading...</td></tr>
            ) : filteredLogs.length > 0 ? (
              filteredLogs.map((log) => {
                const driver = partners.get(log.customerId.toString());
                return (
                  <tr key={log.id} className="hover:bg-gray-100">
                    <td className="px-6 py-5 border-b text-sm">{log.orderId}</td>
                    <td className="px-6 py-5 border-b text-sm">{log.customerId}</td>
                    <td className="px-6 py-5 border-b text-sm">{driver?.name || 'N/A'}</td>
                    <td className="px-6 py-5 border-b text-sm">{driver?.phone || 'N/A'}</td>
                    <td className="px-6 py-5 border-b text-sm">₹{log.amount}</td>
                    <td className="px-6 py-5 border-b text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        log.orderStatus === 'CHARGED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {log.orderStatus}
                      </span>
                    </td>
                    <td className="px-6 py-5 border-b text-sm">{new Date(log.createdAt).toLocaleString()}</td>
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="7" className="text-center py-10">No logs found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default JuspayLogsView;